// Aria — conversational lead-gen wizard (behind ?aria=1). Streams Aria from
// /api/public/wizard-ai/chat, merges her cumulative captured fields into the
// wizard state each turn, and submits via the normal pipeline on completion.
import type { StepContext } from "./index";
import type { WizardState } from "../types";
import { submitWizard } from "./submit";
import { mapAriaToState, ariaReadyToSubmit, type AriaCaptured } from "./aria-map";
import { loadTurnstile, TURNSTILE_SITE_KEY, waitForTurnstileToken } from "./turnstile";

const ENDPOINT = "/api/public/wizard-ai/chat";

interface ChatMsg { role: "user" | "assistant"; content: string }

// Module-scoped for the same reason as step-4-review.ts's widget id — guards
// against a stray duplicate widget if mountAria is ever called more than once.
let turnstileWidgetId: string | null = null;

export function mountAria(host: HTMLElement, ctx: StepContext): void {
  const baseState: WizardState = JSON.parse(JSON.stringify(ctx.state)) as WizardState;
  const history: ChatMsg[] = [];
  let captured: AriaCaptured = {};
  let submitted = false;
  let busy = false;

  host.innerHTML = `
    <div class="wiz-aria" data-role="aria">
      <div class="wiz-aria__log" data-role="log" aria-live="polite"></div>
      <form class="wiz-aria__bar" data-role="bar">
        <textarea class="wiz-aria__input" data-role="input" rows="1" placeholder="Type your reply…" autocomplete="off"></textarea>
        <button type="submit" class="wiz-aria__send" data-role="send" aria-label="Send">→</button>
      </form>
      <div data-role="turnstile"></div>
    </div>
  `;
  const log = host.querySelector<HTMLElement>("[data-role=log]")!;

  // Loaded at mount time so the token is ready well before the conversation
  // reaches a submittable state (Aria's own turn-taking gives Turnstile's
  // invisible `interaction-only` check plenty of time to resolve).
  let turnstileToken = "";
  const turnstileHost = host.querySelector<HTMLElement>("[data-role=turnstile]")!;
  loadTurnstile()
    .then((ts) => {
      if (turnstileWidgetId) {
        try { ts.remove(turnstileWidgetId); } catch { /* widget already gone */ }
      }
      turnstileWidgetId = ts.render(turnstileHost, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: "interaction-only",
        callback: (token: string) => {
          turnstileToken = token;
          console.log("[wizard] Turnstile solved, token length", token.length);
        },
        "expired-callback": () => {
          console.warn("[wizard] Turnstile token expired — requesting a fresh one");
          turnstileToken = "";
          if (turnstileWidgetId) ts.reset(turnstileWidgetId);
        },
        "error-callback": (code?: string) => {
          console.warn("[wizard] Turnstile error-callback", code);
          turnstileToken = "";
          if (turnstileWidgetId) ts.reset(turnstileWidgetId);
        },
      });
    })
    .catch((e) => console.error("[wizard] Turnstile failed to load:", e));
  const form = host.querySelector<HTMLFormElement>("[data-role=bar]")!;
  const input = host.querySelector<HTMLTextAreaElement>("[data-role=input]")!;
  const sendBtn = host.querySelector<HTMLButtonElement>("[data-role=send]")!;

  const bubble = (who: "aria" | "me", text: string): HTMLElement => {
    const el = document.createElement("div");
    el.className = `wiz-aria__bubble wiz-aria__bubble--${who}`;
    el.textContent = text;
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  };

  const setBusy = (b: boolean): void => {
    busy = b;
    input.disabled = b || submitted;
    sendBtn.disabled = b || submitted;
  };

  async function streamTurn(userText: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    if (userText) { bubble("me", userText); history.push({ role: "user", content: userText }); }
    const aria = bubble("aria", "");
    aria.classList.add("is-streaming");
    let full = "";

    try {
      const res = await fetch(ENDPOINT, {
        method: "POST", credentials: "omit",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ retailer: ctx.state.retailerId, message: userText, history }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          const line = ev.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          let obj: Record<string, unknown>;
          try { obj = JSON.parse(line); } catch { continue; }
          if (typeof obj.t === "string") { full += obj.t; aria.textContent = full; log.scrollTop = log.scrollHeight; }
          if (obj.captured && typeof obj.captured === "object") {
            captured = { ...captured, ...(obj.captured as AriaCaptured) };
            ctx.setState(mapAriaToState(captured, baseState));
          }
          if (typeof obj.error === "string" && !full) { full = obj.error; aria.textContent = full; }
        }
      }
    } catch {
      if (!full) aria.textContent = "Sorry, I had a hiccup there — could you say that again?";
    }

    aria.classList.remove("is-streaming");
    if (full) history.push({ role: "assistant", content: full });
    setBusy(false);
    input.focus();

    if (!submitted && ariaReadyToSubmit(captured)) await finish();
  }

  async function finish(): Promise<void> {
    submitted = true;
    setBusy(false);
    input.disabled = true; sendBtn.disabled = true;
    const note = bubble("aria", "✨ Creating your kitchen render now…");
    // Same race as step-4-review.ts's submit button, minus a button to gate:
    // a fast conversation can reach ariaReadyToSubmit() before Turnstile's
    // script-load + render + invisible-solve sequence finishes. Give it a
    // short window rather than sending an empty token outright.
    const token = await waitForTurnstileToken(() => turnstileToken);
    const result = await submitWizard(ctx.state, token);
    if (result.ok) {
      note.textContent = "✨ Your kitchen render is being created — check your email shortly!";
    } else {
      note.textContent = "I couldn't quite send that through — your kitchen designer will follow up. " + (result.error || "");
    }
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || busy || submitted) return;
    input.value = ""; input.style.height = "auto";
    void streamTurn(text);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); }
  });
  input.addEventListener("input", () => {
    input.style.height = "auto";
    input.style.height = `${Math.min(120, input.scrollHeight)}px`;
  });

  // Aria opens the conversation.
  void streamTurn("");
}
